import type {
	AnchorHTMLAttributes,
	KeyboardEventHandler,
	MouseEventHandler,
} from 'react';

import { isHoldingNewTabKey } from 'utils/helpers';

import Link, { type Props as LinkProps } from './Link';

/**
 * Anchor link that can behave like a button.
 *
 * Is a regular link by default but will set an ARIA role and handle button
 * keyboard interactions if an `onClick` handler is set.
 */
export default function ButtonLink({
	children,
	onClick,
	onKeyDown,
	ref,
	...attrs
}: LinkProps) {
	const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
		// Let the natural link click event pass through for new tabs.
		if (isHoldingNewTabKey(e)) {
			return;
		}
		if (onClick) {
			e.preventDefault();
			onClick(e);
		}
	};

	const handleKeydown: KeyboardEventHandler<HTMLAnchorElement> = (e) => {
		// Spacebar triggers native buttons.
		if (e.key === ' ') {
			e.preventDefault();
			e.currentTarget.click();
		}
		onKeyDown?.(e);
	};

	const buttonProps: AnchorHTMLAttributes<HTMLAnchorElement> = {};
	if (onClick) {
		buttonProps.role = 'button';
		buttonProps.onClick = handleClick;
		buttonProps.onKeyDown = handleKeydown;
	}
	if (onKeyDown) {
		buttonProps.onKeyDown = handleKeydown;
	}

	return (
		<Link {...attrs} {...buttonProps} ref={ref}>
			{children}
		</Link>
	);
}
ButtonLink.displayName = 'ButtonLink';
